import { useCallback, useContext, useState } from "react";
import { api } from "../../../api";
import { OverflowFrame } from "../../../externalComponents";
import { PageHeading } from "../../../externalComponents/PageHeading";
import { SettingsSidebar } from "../../../externalComponents/SettingsSidebar";
import {
  Description,
  Field,
  Label,
} from "../../../externalComponents/tailwind/fieldset";
import SelectInput from "../../../externalComponents/tailwind/select-input";
import { CustomerContext } from "../../../providers";
import { Divider } from "../../../externalComponents/tailwind/divider";

const OPTIN_EXPIRATION_PERIOD_OPTIONS = [1, 2, 3, 4, 5].map((year) => ({
  label: `${year} year${year > 1 ? "s" : ""}`,
  value: year,
}));

const ANONYMIZATION_DELAY_MAX_YEARS = 5;

export default function GdprPage() {
  const { customer } = useContext(CustomerContext);
  const [optinExpirationPeriodInYears, setOptinExpirationPeriodInYears] =
    useState(customer.optinExpirationPeriodInYears ?? 2);
  const [anonymizationDelayInYears, setAnonymizationDelayInYears] = useState(
    customer.anonymizationDelayInYears ?? optinExpirationPeriodInYears,
  );

  const handleOptinExpirationPeriodChange = useCallback(
    async (value) => {
      const intValue = parseInt(value, 10);
      setOptinExpirationPeriodInYears(intValue);
      // Anonymization delay can never be shorter than the optin expiration period.
      const nextAnonymizationDelay = Math.max(
        anonymizationDelayInYears,
        intValue,
      );
      setAnonymizationDelayInYears(nextAnonymizationDelay);
      await api.customer.update(customer.id, {
        optinExpirationPeriodInYears: intValue,
        anonymizationDelayInYears: nextAnonymizationDelay,
      });
    },
    [customer.id, anonymizationDelayInYears],
  );

  const handleAnonymizationDelayChange = useCallback(
    async (value) => {
      const intValue = parseInt(value, 10);
      setAnonymizationDelayInYears(intValue);
      await api.customer.update(customer.id, {
        anonymizationDelayInYears: intValue,
      });
    },
    [customer.id],
  );

  const anonymizationDelayOptions = Array.from(
    { length: ANONYMIZATION_DELAY_MAX_YEARS },
    (_, i) => i + 1,
  )
    .filter((year) => year >= optinExpirationPeriodInYears)
    .map((year) => ({
      label: `${year} year${year > 1 ? "s" : ""}`,
      value: year,
    }));

  return (
    <>
      <SettingsSidebar>
        <PageHeading title="GDPR" />

        <OverflowFrame>
          <div className="my-5">
            <Field >
              <div  className="w-40">
                <Label>Opt-in expiration period</Label>
                <div className="my-2">
                  <SelectInput
                    options={OPTIN_EXPIRATION_PERIOD_OPTIONS}
                    value={optinExpirationPeriodInYears}
                    selected={optinExpirationPeriodInYears}
                    onChange={handleOptinExpirationPeriodChange}
                  />
                </div>
              </div>

              <Description>
                Contacts who have not interacted for longer than this period
                will be automatically opted out.
              </Description>
            </Field>

          </div>
          <Divider />

          <div className="my-5">
            <Field>
              <div className="w-40">
                <Label>Anonymization delay</Label>
                <div className="my-2">
                  <SelectInput
                    options={anonymizationDelayOptions}
                    value={anonymizationDelayInYears}
                    selected={anonymizationDelayInYears}
                    onChange={handleAnonymizationDelayChange}
                  />
                </div>
              </div>

              <Description>
                Contacts opted out for exceeding the opt-in expiration period
                above will have their personal data permanently erased after
                this delay (it can't be shorter than the opt-in expiration
                period). If set to the same value, anonymization happens 24
                hours after the opt-out.
              </Description>
            </Field>
          </div>
          <Divider />
        </OverflowFrame>
      </SettingsSidebar>
    </>
  );
}
